// components/DebouncedSearchInput.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { debounce } from 'lodash';

const DebouncedSearchInput = ({
  placeholder = 'Search...',
  delay = 300,
  onSearch,
  className = '',
  inputClassName = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      onSearch(term);
      setIsSearching(false);
    }, delay),
    [onSearch, delay]
  );

  // Handle input change
  const handleChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsSearching(true);
    debouncedSearch(term);
  };

  // Clear search
  const handleClear = () => {
    setSearchTerm('');
    setIsSearching(false);
    onSearch('');
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className={`relative ${className}`}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleChange}
        className={`block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm ${inputClassName}`}
      />
      {(searchTerm || isSearching) && (
        <button
          onClick={handleClear}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          aria-label="Clear search"
        >
          {isSearching ? (
            <span className="animate-spin h-5 w-5 border-2 border-teal-500 border-t-transparent rounded-full"></span>
          ) : (
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
          )}
        </button>
      )}
    </div>
  );
};

export default DebouncedSearchInput;